// Chat API Helper Functions
const ChatAPI = {
  getCSRFToken() {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
  },

  async fetchConversations(cacheBust = false) {
    try {
      const url = cacheBust ? `/api/chat/conversations?_t=${Date.now()}` : '/api/chat/conversations';
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCSRFToken(),
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.conversations || [];
    } catch (error) {
      console.error('Error fetching conversations:', error);
      return [];
    }
  },

  async fetchConversation(conversationId) {
    try {
      const response = await fetch(`/api/chat/conversations/${conversationId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCSRFToken(),
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching conversation:', error);
      return null;
    }
  },

  async fetchMessages(conversationId, page = 1, perPage = 50) {
    try {
      const response = await fetch(`/api/chat/conversations/${conversationId}/messages?page=${page}&per_page=${perPage}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCSRFToken(),
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching messages:', error);
      return {messages: [], pagination: {}};
    }
  },

  determineAttachmentType(fileType) {
    if (!fileType) return 'document';
    const type = fileType.toLowerCase();
    if (type.startsWith('image/')) return 'image';
    if (type.startsWith('video/')) return 'video';
    return 'document';
  },

  async sendMessage(conversationId, content, attachments = [], onProgress = null) {
    const formData = new FormData();
    formData.append('content', content || '');
    const hasFiles = attachments.some(a => a && (a.file instanceof File || a.file instanceof Blob));

    attachments.forEach((attachment, index) => {
      if (attachment.file) {
        const file = attachment.file;
        const fileName = attachment.file_name || (file.name || 'upload');
        const fileType = attachment.file_type || (file.type || '');
        const attachmentType = attachment.attachment_type || this.determineAttachmentType(fileType);
        formData.append(`attachments[${index}][file]`, file, fileName);
        formData.append(`attachments[${index}][file_name]`, fileName);
        formData.append(`attachments[${index}][file_type]`, fileType);
        formData.append(`attachments[${index}][file_size]`, String(attachment.file_size ?? file.size ?? 0));
        formData.append(`attachments[${index}][attachment_type]`, attachmentType);
      } else if (attachment.url) {
        const fileType = attachment.file_type || 'image/png';
        const attachmentType = attachment.attachment_type || this.determineAttachmentType(fileType);
        formData.append(`attachments[${index}][file]`, attachment.url);
        formData.append(`attachments[${index}][file_name]`, attachment.file_name || 'file');
        formData.append(`attachments[${index}][file_type]`, fileType);
        formData.append(`attachments[${index}][file_size]`, attachment.file_size || 0);
        formData.append(`attachments[${index}][attachment_type]`, attachmentType);
      }
    });

    if (hasFiles && typeof onProgress === 'function') {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        const url = `/api/chat/conversations/${conversationId}/messages`;
        xhr.open('POST', url);
        xhr.setRequestHeader('X-CSRF-Token', this.getCSRFToken());
        xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
        xhr.withCredentials = true;

        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const percent = Math.round((e.loaded / e.total) * 100);
            onProgress(percent);
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              resolve(JSON.parse(xhr.responseText));
            } catch (_) {
              reject(new Error('Invalid response'));
            }
          } else {
            let errMsg = `Request failed (${xhr.status})`;
            try {
              const errorData = JSON.parse(xhr.responseText);
              errMsg = errorData.error || errorData.errors?.join(', ') || errMsg;
            } catch (_) {
            }
            reject(new Error(errMsg));
          }
        });
        xhr.addEventListener('error', () => reject(new Error('Network error')));
        xhr.addEventListener('abort', () => reject(new Error('Upload cancelled')));
        xhr.send(formData);
      });
    }

    try {
      const response = await fetch(`/api/chat/conversations/${conversationId}/messages`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': this.getCSRFToken(),
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin',
        body: formData
      });

      if (!response.ok) {
        let errMsg = `Request failed (${response.status})`;
        try {
          const errorData = await response.json();
          errMsg = errorData.error || errorData.errors?.join(', ') || errMsg;
        } catch (_) {
        }
        throw new Error(errMsg);
      }

      return await response.json();
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  },

  async createDirectConversation(userId) {
    try {
      const response = await fetch('/api/chat/conversations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCSRFToken(),
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          conversation_type: 'direct',
          user_id: userId
        })
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        return { error: data.error || 'Unable to start chat.', status: response.status };
      }
      return data;
    } catch (error) {
      console.error('Error creating direct conversation:', error);
      return { error: 'Failed to create conversation. Please try again.', status: 0 };
    }
  },

  async createGroupConversation(name, category, organizationId, userIds) {
    try {
      const response = await fetch('/api/chat/conversations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCSRFToken(),
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          conversation_type: 'group_chat',
          name: name,
          category: category,
          organization_id: organizationId,
          user_ids: userIds
        })
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        const msg = body.error || `HTTP error! status: ${response.status}`;
        throw new Error(msg);
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating group conversation:', error);
      throw error;
    }
  },

  async addMembers(conversationId, userIds) {
    try {
      const response = await fetch(`/api/chat/conversations/${conversationId}/add_members`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCSRFToken(),
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin',
        body: JSON.stringify({ user_ids: userIds })
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error adding members:', error);
      throw error;
    }
  },

  async fetchEligibleMembers(conversationId) {
    try {
      const response = await fetch(`/api/chat/conversations/${conversationId}/eligible_members`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCSRFToken(),
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.users || [];
    } catch (error) {
      console.error('Error fetching eligible members:', error);
      return [];
    }
  },

  async leaveConversation(conversationId) {
    try {
      const response = await fetch(`/api/chat/conversations/${conversationId}/leave`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCSRFToken(),
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error leaving conversation:', error);
      throw error;
    }
  },

  async deleteConversation(conversationId) {
    try {
      const response = await fetch(`/api/chat/conversations/${conversationId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCSRFToken(),
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error deleting conversation:', error);
      throw error;
    }
  },

  async undoMessage(conversationId, messageId) {
    try {
      const response = await fetch(`/api/chat/conversations/${conversationId}/messages/${messageId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCSRFToken(),
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin'
      });
      const data = await response.json().catch(() => ({}));
      return { ok: response.ok, status: response.status, error: data.error };
    } catch (error) {
      console.error('Error undoing message:', error);
      return { ok: false, status: 0, error: null };
    }
  },

  async markAllRead(conversationId) {
    try {
      const response = await fetch(`/api/chat/conversations/${conversationId}/mark_all_read`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCSRFToken(),
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error marking all read:', error);
      throw error;
    }
  },

  async getConversationDetails(conversationId) {
    try {
      const response = await fetch(`/api/chat/conversations/${conversationId}/details`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCSRFToken(),
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching conversation details:', error);
      throw error;
    }
  },

  async searchConversations(query, conversationId = null, tab = '') {
    try {
      const url = conversationId
        ? `/api/chat/conversations/${conversationId}/messages/search?query=${encodeURIComponent(query)}`
        : `/api/chat/search?query=${encodeURIComponent(query)}${tab ? `&tab=${encodeURIComponent(tab)}` : ''}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCSRFToken(),
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error searching:', error);
      return {results: []};
    }
  },

  async getChatSettings() {
    try {
      const response = await fetch('/api/chat/settings', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCSRFToken(),
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching chat settings:', error);
      return {
        allow_desktop_notifications: true,
        notification_sound: true,
        email_notifications: true
      };
    }
  },

  async updateChatSettings(settings) {
    try {
      const response = await fetch('/api/chat/settings', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCSRFToken(),
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin',
        body: JSON.stringify(settings)
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.errors?.join(', ') || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating chat settings:', error);
      throw error;
    }
  },

  async getNotifications() {
    try {
      const response = await fetch('/api/chat/notifications', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCSRFToken(),
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching notifications:', error);
      return {notifications: [], unread_count: 0};
    }
  },

  async markNotificationRead(notificationId) {
    try {
      const response = await fetch(`/api/chat/notifications/${notificationId}/read`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCSRFToken(),
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error marking notification read:', error);
      throw error;
    }
  },

  async fetchCities(query = '', tab = '') {
    try {
      const url = new URL('/api/chat/contacts/cities', window.location.origin);
      if (query && query.trim()) url.searchParams.set('q', query.trim());
      if (tab) url.searchParams.set('tab', tab);
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCSRFToken(),
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.cities || [];
    } catch (error) {
      console.error('Error fetching cities:', error);
      return [];
    }
  },

  async fetchGroups(query = '', tab = '') {
    try {
      const url = new URL('/api/chat/contacts/groups', window.location.origin);
      if (query && query.trim()) url.searchParams.set('q', query.trim());
      if (tab) url.searchParams.set('tab', tab);
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCSRFToken(),
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.groups || [];
    } catch (error) {
      console.error('Error fetching groups:', error);
      return [];
    }
  },

  async fetchIndividuals(query = '', tab = '') {
    try {
      const url = new URL('/api/chat/contacts/individuals', window.location.origin);
      if (query && query.trim()) url.searchParams.set('q', query.trim());
      if (tab) url.searchParams.set('tab', tab);
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCSRFToken(),
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.individuals || [];
    } catch (error) {
      console.error('Error fetching individuals:', error);
      return [];
    }
  },

  async fetchCityUsers(organizationId) {
    try {
      const response = await fetch(`/api/chat/contacts/city_users?organization_id=${organizationId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCSRFToken(),
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.users || [];
    } catch (error) {
      console.error('Error fetching city users:', error);
      return [];
    }
  }
};

export default ChatAPI;
